const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const bodyLocks = new WeakMap();
let activeOverlay = null;

export function acquireBodyScrollLock(ownerDocument = document) {
  let state = bodyLocks.get(ownerDocument);
  if (!state) {
    state = { count: 0, previous: ownerDocument.body.style.overflow };
    bodyLocks.set(ownerDocument, state);
  }
  state.count += 1;
  ownerDocument.body.style.overflow = 'hidden';
  let released = false;
  return () => {
    if (released) return;
    released = true;
    state.count -= 1;
    if (state.count === 0) {
      ownerDocument.body.style.overflow = state.previous;
      bodyLocks.delete(ownerDocument);
    }
  };
}

export function claimOverlay(close) {
  activeOverlay?.close();
  const token = { close };
  activeOverlay = token;
  return () => { if (activeOverlay === token) activeOverlay = null; };
}

export function emit(target, name, detail = {}) {
  return target.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
}

export function sectionLifecycle({ mount, unmount = () => {} }, root = document) {
  const onLoad = (event) => mount(event.target);
  const onUnload = (event) => unmount(event.target);
  mount(root);
  root.addEventListener('shopify:section:load', onLoad);
  root.addEventListener('shopify:section:unload', onUnload);
  return () => {
    root.removeEventListener('shopify:section:load', onLoad);
    root.removeEventListener('shopify:section:unload', onUnload);
    unmount(root);
  };
}

export function restoreFocus(target) {
  if (target && target.isConnected !== false && typeof target.focus === 'function') target.focus();
}

export function isRendered(item) {
  if (typeof item.getClientRects !== 'function') return true;
  if (item.closest?.('[hidden], [inert]')) return false;
  return item.getClientRects().length > 0;
}

export function trapFocus(container, returnTo = typeof document === 'undefined' ? null : document.activeElement) {
  const ownerDocument = container.ownerDocument || (typeof document === 'undefined' ? null : document);
  // Only controls that are actually rendered count: a hidden trailing control (e.g. an empty results list) would otherwise
  // be treated as "last", so Tab from the real last control would leave the dialog.
  const focusable = () => [...container.querySelectorAll(focusableSelector)].filter((item) => item.getAttribute('aria-hidden') !== 'true' && isRendered(item));
  const firstFocusable = focusable()[0];
  const assignedTabindex = !firstFocusable && !container.hasAttribute('tabindex');
  if (assignedTabindex) container.setAttribute('tabindex', '-1');
  const initial = firstFocusable || container;
  initial.focus?.();
  const onKeydown = (event) => {
    if (event.key !== 'Tab') return;
    const items = focusable();
    if (items.length === 0) {
      event.preventDefault();
      container.focus?.();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === container)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  const onFocusIn = (event) => {
    if (!container.contains(event.target)) (focusable()[0] || container).focus?.();
  };
  container.addEventListener('keydown', onKeydown);
  ownerDocument?.addEventListener?.('focusin', onFocusIn);
  return () => {
    container.removeEventListener('keydown', onKeydown);
    ownerDocument?.removeEventListener?.('focusin', onFocusIn);
    if (assignedTabindex) container.removeAttribute('tabindex');
    restoreFocus(returnTo);
  };
}

export function setDisclosure(trigger, panel, open) {
  trigger.setAttribute('aria-expanded', String(open));
  if (panel.id) trigger.setAttribute('aria-controls', panel.id);
  panel.hidden = !open;
  return open;
}

export function openDialog(dialog, trigger) {
  let releaseOverlay = () => {};
  let releaseFocus = () => {};
  let finished = false;
  const onClose = () => {
    if (finished) return;
    finished = true;
    releaseOverlay();
    dialog.removeEventListener('close', onClose);
    dialog.removeEventListener('keydown', onKeydown);
    releaseFocus();
  };
  const close = () => {
    if (finished) return;
    if (typeof dialog.close === 'function') dialog.close();
    else {
      dialog.hidden = true;
      emit(dialog, 'close');
    }
    onClose();
  };
  releaseOverlay = claimOverlay(close);
  if (typeof dialog.showModal === 'function') dialog.showModal();
  else {
    dialog.hidden = false;
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
  }
  releaseFocus = trapFocus(dialog, trigger);
  const onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    close();
  };
  dialog.addEventListener('keydown', onKeydown);
  dialog.addEventListener('close', onClose);
  return close;
}

export function prefersReducedMotion() {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function createRequest(url, options = {}) {
  const controller = new AbortController();
  const onExternalAbort = () => controller.abort();
  if (options.signal?.aborted) controller.abort();
  else options.signal?.addEventListener('abort', onExternalAbort, { once: true });
  const { signal: externalSignal, ...fetchOptions } = options;
  const promise = fetch(url, { ...fetchOptions, signal: controller.signal })
    .then((response) => {
      if (!response.ok) throw new Error(`Request failed (${response.status})`);
      return response.json();
    })
    .finally(() => externalSignal?.removeEventListener('abort', onExternalAbort));
  return { promise, abort: () => controller.abort(), signal: controller.signal };
}

if (typeof document !== 'undefined') document.documentElement.classList.replace('no-js', 'js');
